import { forwardRef, useEffect, useImperativeHandle, useRef } from "react";
import type { CuttlefishConnection } from "../types";
import { useCuttlefishDeviceStreams } from "../hooks";

export interface CuttlefishAudioRef {
  /**
   * Play all audio elements
   */
  play: () => Promise<void>;
  /**
   * Pause all audio elements
   */
  pause: () => void;
  /**
   * Get all audio elements
   */
  getElements: () => HTMLAudioElement[];
  /**
   * Check if any audio is playing
   */
  isPlaying: () => boolean;
}

export interface CuttlefishAudioProps {
  connection: CuttlefishConnection | null;
}

/**
 * CuttlefishAudio Component
 *
 * Manages audio stream elements for a Cuttlefish device connection.
 * Creates hidden <audio> elements for each audio stream and handles their lifecycle.
 * Exposes imperative methods via ref to control audio playback.
 *
 * @example
 * ```tsx
 * function MyComponent() {
 *   const { connection } = useCuttlefish();
 *   const audioRef = useRef<CuttlefishAudioRef>(null);
 *
 *   const handleVolumeOff = () => {
 *     if (audioRef.current) {
 *       audioRef.current.pause();
 *     }
 *   };
 *
 *   const handleVolumeOn = () => {
 *     if (audioRef.current) {
 *       audioRef.current.play();
 *     }
 *   };
 *
 *   return <CuttlefishAudio ref={audioRef} connection={connection} />;
 * }
 * ```
 */
export const CuttlefishAudio = forwardRef<CuttlefishAudioRef, CuttlefishAudioProps>(
  ({ connection }, ref) => {
    const deviceStreams = useCuttlefishDeviceStreams(connection);
    const audioElementsRef = useRef<HTMLAudioElement[]>([]);

    useEffect(() => {
      if (!deviceStreams.audioStream) return;

      const audioElement = document.createElement("audio");
      audioElement.classList.add("device-audio");
      audioElement.srcObject = deviceStreams.audioStream;
      audioElement.autoplay = true;

      // Add to refs
      audioElementsRef.current.push(audioElement);

      // Append to document (hidden)
      document.body.appendChild(audioElement);

      // Start playing
      audioElement
        .play()
        .catch((e) => console.error("[CuttlefishAudio] Audio autoplay error:", e));

      return () => {
        audioElement.pause();
        audioElement.srcObject = null;
        if (document.body.contains(audioElement)) {
          document.body.removeChild(audioElement);
        }
        audioElementsRef.current = audioElementsRef.current.filter(
          (el) => el !== audioElement
        );
      };
    }, [deviceStreams.audioStream]);

    // Expose imperative methods via ref
    useImperativeHandle(
      ref,
      () => ({
        play: async () => {
          const playPromises = audioElementsRef.current.map((audioElem) =>
            audioElem.play().catch((e) => console.error("[CuttlefishAudio] Play error:", e))
          );
          await Promise.all(playPromises);
        },
        pause: () => {
          audioElementsRef.current.forEach((audioElem) => {
            audioElem.pause();
          });
        },
        getElements: () => {
          return audioElementsRef.current;
        },
        isPlaying: () => {
          return audioElementsRef.current.some((audioElem) => !audioElem.paused);
        },
      }),
      []
    );

    // This component doesn't render anything visible
    return null;
  }
);

CuttlefishAudio.displayName = "CuttlefishAudio";
